import React, { useRef, useEffect } from "react";
import { FiBold, FiItalic, FiUnderline, FiList, FiLink } from "react-icons/fi";

// Éditeur de texte riche léger (WYSIWYG) — sans dépendance externe.
// Produit du HTML, compatible avec le rendu DynamicPage (.cms-content).
const RichTextEditor = ({ value, onChange, dir = "ltr" }) => {
  const ref = useRef(null);

  // Initialise / resynchronise le contenu quand il change DE L'EXTÉRIEUR
  // (ex. ouverture d'une autre page). Évite le saut de curseur pendant la frappe.
  useEffect(() => {
    if (ref.current && (value || "") !== ref.current.innerHTML) {
      ref.current.innerHTML = value || "";
    }
  }, [value]);

  const emit = () => {
    if (ref.current) onChange(ref.current.innerHTML);
  };

  const exec = (cmd, arg) => {
    try { document.execCommand(cmd, false, arg); } catch (e) { /* ignore */ }
    if (ref.current) ref.current.focus();
    emit();
  };

  const onFocusInit = () => {
    // Enter crée des <p> (et non des <div>) -> styles cohérents côté public.
    try { document.execCommand("defaultParagraphSeparator", false, "p"); } catch (e) {}
  };

  const addLink = () => {
    const url = window.prompt("URL du lien (ex. https://… ou /contact) :", "https://");
    if (url) exec("createLink", url);
  };

  // Empêche le bouton de voler le focus -> la sélection dans l'éditeur est préservée.
  const noFocus = (e) => e.preventDefault();

  const Btn = ({ onClick, title, children }) => (
    <button
      type="button"
      title={title}
      onMouseDown={noFocus}
      onClick={onClick}
      className="px-2.5 py-1.5 rounded hover:bg-gray-100 text-gray-700 text-sm font-semibold min-w-[34px] flex items-center justify-center"
    >
      {children}
    </button>
  );

  return (
    <div className="border border-gray-300 rounded-lg overflow-hidden">
      <div className="flex flex-wrap items-center gap-0.5 border-b border-gray-200 bg-gray-50 px-2 py-1">
        <Btn onClick={() => exec("bold")} title="Gras"><FiBold size={15} /></Btn>
        <Btn onClick={() => exec("italic")} title="Italique"><FiItalic size={15} /></Btn>
        <Btn onClick={() => exec("underline")} title="Souligné"><FiUnderline size={15} /></Btn>
        <span className="mx-1 w-px h-5 bg-gray-300" />
        <Btn onClick={() => exec("formatBlock", "<h2>")} title="Titre">H2</Btn>
        <Btn onClick={() => exec("formatBlock", "<h3>")} title="Sous-titre">H3</Btn>
        <Btn onClick={() => exec("formatBlock", "<p>")} title="Paragraphe">P</Btn>
        <span className="mx-1 w-px h-5 bg-gray-300" />
        <Btn onClick={() => exec("insertUnorderedList")} title="Liste à puces"><FiList size={15} /></Btn>
        <Btn onClick={() => exec("insertOrderedList")} title="Liste numérotée">1.</Btn>
        <span className="mx-1 w-px h-5 bg-gray-300" />
        <Btn onClick={addLink} title="Insérer un lien"><FiLink size={15} /></Btn>
        <Btn onClick={() => exec("removeFormat")} title="Effacer la mise en forme">Tx</Btn>
      </div>

      <div
        ref={ref}
        contentEditable
        suppressContentEditableWarning
        dir={dir}
        onInput={emit}
        onBlur={emit}
        onFocus={onFocusInit}
        className="cms-editor min-h-[220px] px-4 py-3 outline-none text-gray-800 leading-relaxed"
        style={{ direction: dir }}
      />

      <style>{`
        .cms-editor h2 { font-size: 1.125rem; font-weight: 700; margin: .75rem 0 .35rem; }
        .cms-editor h3 { font-size: 1rem; font-weight: 700; margin: .6rem 0 .3rem; }
        .cms-editor p { margin-bottom: .5rem; }
        .cms-editor ul { list-style: disc; padding-inline-start: 1.4rem; }
        .cms-editor ol { list-style: decimal; padding-inline-start: 1.4rem; }
        .cms-editor a { color: #e11d7b; text-decoration: underline; }
        .cms-editor:empty:before { content: attr(data-placeholder); color: #9ca3af; }
      `}</style>
    </div>
  );
};

export default RichTextEditor;
